import React, { useEffect, useState } from "react";
import { Loader2, X, FileDown, Gavel, Trophy, Send } from "lucide-react";
import api, { apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TERMS = [
  ["quote_ref", "Quotation ref"],
  ["validity_days", "Validity (days)"],
  ["delivery_period", "Delivery period"],
  ["payment_terms", "Payment terms"],
  ["inclusions", "Inclusions"],
  ["exclusions", "Exclusions"],
  ["origin", "Country of origin"],
  ["warranty", "Warranty"],
  ["performance_security", "Performance security"],
  ["notes", "Notes"],
];

export const VendorQuoteDialog = ({ rfqId, quoteId, onClose, onChanged }) => {
  const [q, setQ] = useState(null);
  const [asking, setAsking] = useState(false);
  const [showBafo, setShowBafo] = useState(false);
  const [message, setMessage] = useState("");
  const [deadline, setDeadline] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/buyer/rfqs/${rfqId}/quotes/${quoteId}`);
      setQ(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not open that quotation");
      onClose();
    }
  };
  useEffect(() => { load(); }, [quoteId]);

  const downloadPdf = () => {
    const token = localStorage.getItem("matiqs_token");
    fetch(`${API}/buyer/rfqs/${rfqId}/quotes/${quoteId}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = `${q?.vendor_name || "quotation"}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error("This vendor's quotation PDF is not available"));
  };

  const askBafo = async () => {
    setAsking(true);
    try {
      const { data } = await api.post(`/buyer/rfqs/${rfqId}/quotes/${quoteId}/bafo`, { message, deadline });
      toast.success(data.emailed
        ? `Best and final requested — ${q.vendor_name} was emailed`
        : "Best and final requested — this vendor has no email on file, tell them directly");
      setShowBafo(false);
      setMessage("");
      setDeadline("");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not send that request");
    } finally {
      setAsking(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 sm:p-8 bg-slate-950/60 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div
        data-testid="vendor-quote-dialog"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-4xl bg-card border border-border rounded-2xl shadow-2xl my-4"
      >
        {!q ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-border">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div data-testid="quote-dialog-vendor" className="font-display text-lg font-bold text-slate-900 dark:text-slate-100 truncate">
                    {q.vendor_name}
                  </div>
                  {q.awarded && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider">
                      <Trophy className="w-3 h-3" /> Awarded
                    </span>
                  )}
                  {q.revision > 1 && (
                    <span className="px-2 py-0.5 rounded-md bg-primary/10 text-primary text-[10px] font-bold uppercase tracking-wide">
                      Submission {q.revision}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {q.rfq.rfq_ref} · {q.source === "matiqs" ? "Submitted via Matiqs"
                    : q.source === "excel_ai" ? "Excel read by AI"
                    : q.source === "pdf_ai" ? "PDF read by AI" : "Entered manually"}
                  {q.quote_ref ? ` · ${q.quote_ref}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {q.has_pdf && (
                  <Button variant="outline" data-testid="quote-dialog-pdf" onClick={downloadPdf} className="h-9 gap-2 font-semibold">
                    <FileDown className="w-4 h-4" /> Their PDF
                  </Button>
                )}
                {!q.awarded && (
                  <Button variant="outline" data-testid="ask-bafo-button" onClick={() => setShowBafo(!showBafo)} className="h-9 gap-2 font-semibold">
                    <Gavel className="w-4 h-4" /> Best & final
                  </Button>
                )}
                <button data-testid="close-quote-dialog" onClick={onClose} className="text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {q.bafo && (
              <div data-testid="bafo-state" className="px-6 py-3 bg-brand-soft/60 border-b border-brand/20 text-xs">
                <span className="font-bold text-brand uppercase tracking-wide">
                  {q.bafo.status === "responded" ? "Best & final received" : "Best & final requested"}
                </span>
                <span className="text-muted-foreground">
                  {q.bafo.deadline ? ` · reply by ${q.bafo.deadline}` : ""}
                  {q.bafo.previous_total ? ` · was ${q.currency} ${money(q.bafo.previous_total)}` : ""}
                </span>
                {q.bafo.message && <span className="block text-muted-foreground italic mt-1">“{q.bafo.message}”</span>}
              </div>
            )}

            {showBafo && (
              <div data-testid="bafo-form" className="px-6 py-5 border-b border-border bg-secondary/40 space-y-3">
                <div>
                  <Label htmlFor="bafo-msg">Message to {q.vendor_name}</Label>
                  <Textarea id="bafo-msg" data-testid="bafo-message-input" rows={3} value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="You are shortlisted. Please review your rates on the glazing items and resubmit your best and final price."
                            className="mt-1.5" />
                </div>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                  <div className="flex-1">
                    <Label htmlFor="bafo-deadline">Reply by</Label>
                    <Input id="bafo-deadline" data-testid="bafo-deadline-input" type="date" value={deadline}
                           onChange={(e) => setDeadline(e.target.value)} className="mt-1.5" />
                  </div>
                  <Button data-testid="send-bafo-button" onClick={askBafo} disabled={asking} className="h-11 gap-2 font-semibold">
                    {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send request
                  </Button>
                </div>
              </div>
            )}

            <div className="px-6 py-5 grid sm:grid-cols-3 gap-4 border-b border-border">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">Total</div>
                <div data-testid="quote-dialog-total" className="font-mono text-2xl font-extrabold tabular-nums mt-1">
                  {q.currency} {money(q.total)}
                </div>
                {q.discount > 0 && <div className="text-xs text-muted-foreground mt-1">after {money(q.discount)} discount</div>}
                {q.extras_total > 0 && (
                  <div className="text-xs text-muted-foreground mt-1">
                    includes {money(q.extras_total)} they added
                  </div>
                )}
                <div className="mt-4 space-y-1 text-xs">
                  {[["Contact", q.contact_person], ["Email", q.email], ["Phone", q.contact_phone]].map(([label, val]) => (
                    <div key={label} className="flex justify-between gap-3">
                      <span className="text-muted-foreground">{label}</span>
                      <span data-testid={`quote-contact-${label.toLowerCase()}`} className="font-semibold text-right truncate">{val || "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2 grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                {TERMS.map(([k, label]) => (
                  <div key={k} className="flex justify-between gap-3 border-b border-border/60 py-1">
                    <span className="text-muted-foreground">{label}</span>
                    <span data-testid={`quote-term-${k}`} className="font-semibold text-right truncate">
                      {String(q.terms?.[k] ?? "") || "—"}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {q.has_lines || (q.extras || []).length > 0 ? (
              <div className="overflow-x-auto max-h-[50vh]">
                <table data-testid="quote-dialog-lines" className="w-full text-sm">
                  <thead className="sticky top-0">
                    <tr className="text-[11px] uppercase tracking-wider text-muted-foreground bg-secondary">
                      <th className="text-left font-bold px-4 py-2.5 w-8">#</th>
                      <th className="text-left font-bold px-4 py-2.5 min-w-[260px]">Description</th>
                      <th className="text-left font-bold px-3 py-2.5">Unit</th>
                      <th className="text-right font-bold px-3 py-2.5">Qty</th>
                      <th className="text-right font-bold px-3 py-2.5">Rate</th>
                      <th className="text-right font-bold px-4 py-2.5">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      let n = 0;
                      return q.lines.map((l, i) => {
                        if (l.kind !== "item") {
                          return (
                            <tr key={l.item_id} className="border-t border-border bg-secondary/40">
                              <td className="px-4 py-2" />
                              <td colSpan={5} className="px-4 py-2 font-bold text-slate-900 dark:text-slate-100"
                                  style={{ paddingLeft: `${16 + (l.level || 0) * 16}px` }}>
                                {l.description}
                              </td>
                            </tr>
                          );
                        }
                        n += 1;
                        return (
                          <tr key={l.item_id} data-testid={`quote-line-${i}`} className="border-t border-border">
                            <td className="px-4 py-2.5 text-xs text-muted-foreground font-mono">{n}</td>
                            <td className="px-4 py-2.5" style={{ paddingLeft: `${16 + (l.level || 0) * 16}px` }}>{l.description}</td>
                            <td className="px-3 py-2.5 text-muted-foreground">{l.unit || "—"}</td>
                            <td className="px-3 py-2.5 text-right font-mono text-muted-foreground">{money(l.qty)}</td>
                            <td className="px-3 py-2.5 text-right font-mono">{l.rate === null ? "—" : money(l.rate)}</td>
                            <td className="px-4 py-2.5 text-right font-mono font-semibold">{l.amount === null ? "—" : money(l.amount)}</td>
                          </tr>
                        );
                      });
                    })()}
                    {(q.extras || []).length > 0 && (
                      <>
                        <tr className="border-t border-border bg-primary/10">
                          <td className="px-4 py-2" />
                          <td colSpan={5} className="px-4 py-2 font-bold text-primary">
                            Added by {q.vendor_name} — outside your BOQ
                          </td>
                        </tr>
                        {q.extras.map((x, j) => (
                          <tr key={x.id || j} data-testid={`quote-extra-${j}`} className="border-t border-border bg-primary/5">
                            <td className="px-4 py-2.5 text-xs text-muted-foreground font-mono">+</td>
                            <td className="px-4 py-2.5 italic">{x.description}</td>
                            <td className="px-3 py-2.5 text-muted-foreground">{x.unit || "—"}</td>
                            <td className="px-3 py-2.5 text-right font-mono text-muted-foreground">{money(x.qty)}</td>
                            <td className="px-3 py-2.5 text-right font-mono">{money(x.rate)}</td>
                            <td className="px-4 py-2.5 text-right font-mono font-semibold">{money(x.amount)}</td>
                          </tr>
                        ))}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="px-6 py-10 text-center text-sm text-muted-foreground">
                This vendor gave headline numbers only — no line-by-line rates were captured.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
